import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Users } from "lucide-react";

export default function AssignParentsModal({ open, onOpenChange, county, onSaved }) {
  const [parents, setParents] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && county) {
      api.get("/billing/parents").then(({ data }) => {
        setParents(data);
        const init = {};
        data.forEach((p) => {
          if (p.county_id === county.id) init[p.id] = true;
        });
        setSelected(init);
      });
    }
  }, [open, county]);

  if (!county) return null;

  const toggle = (id) => setSelected({ ...selected, [id]: !selected[id] });

  const save = async () => {
    setLoading(true);
    const parent_ids = Object.keys(selected).filter((id) => selected[id]);
    try {
      await api.put(`/counties/${county.id}/parents`, { parent_ids });
      toast.success("Caseload updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="assign-parents-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Users className="h-5 w-5 text-[#0d9488]" /> Assign Parents — {county.name}
          </DialogTitle>
          <DialogDescription>Parents linked here roll up into this county's usage & billing.</DialogDescription>
        </DialogHeader>
        <div className="max-h-72 space-y-2 overflow-y-auto">
          {parents.map((p) => {
            const other = p.county_id && p.county_id !== county.id && !selected[p.id];
            return (
              <label
                key={p.id}
                data-testid={`assign-parent-${p.id}`}
                className={`flex items-center gap-3 rounded-lg border p-3 ${selected[p.id] ? "border-[#0d9488] bg-teal-50/40" : "border-slate-200"}`}
              >
                <Checkbox checked={!!selected[p.id]} onCheckedChange={() => toggle(p.id)} data-testid={`assign-parent-check-${p.id}`} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">{p.name}</p>
                  <p className="text-xs text-slate-500">
                    Child: {p.child_name || "N/A"}
                    {other && <span className="ml-1 text-amber-600">· currently: {p.county_name}</span>}
                  </p>
                </div>
              </label>
            );
          })}
          {parents.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No parents registered yet.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="save-assign-parents-btn" onClick={save} disabled={loading} className="bg-[#1e3a8a] hover:bg-[#172554]">
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
